import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@hsc/auth';
import { useTranslation } from '@hsc/i18n';
import {
  buildBottomTabs,
  buildCanonicalNav,
  type NavRole,
  type ResolvedNavGroup,
} from '../../shared/nav/canonical-nav.js';
import { clearLocalTokens } from '../../lib/storage.js';
import { Sidebar } from './Sidebar.js';
import { BottomTabs } from './BottomTabs.js';
import { initials, ROLE_LABEL } from './shell-util.js';
import { useShellHeaderOverride } from './shell-header.js';

/**
 * Каркас рабочего пространства по согласованному дизайну manager v3 §2.2.
 *
 * Раскладка (3 раздельных вьюпорта):
 *   - desktop (≥1024): сгруппированный сайдбар на всю высоту (бренд «LovArt ·
 *     <Отель>» сверху, меню в центре, карточка пользователя + «Выйти» снизу)
 *     + лёгкая строка-шапка контента (крошка + заголовок). ГЛОБАЛЬНОЙ ПОЛОСЫ
 *     email/поиск/колокольчик СВЕРХУ НЕТ.
 *   - tablet (768–1023): сайдбар-DRAWER закрыт (гамбургер); сверху узкая
 *     строка с брендом + «● Live» + аватар-инициалы.
 *   - phone (<768): сайдбара нет; сверху заголовок страницы; нижние sticky
 *     bottom-tabs + гамбургер-drawer со всем меню и «Выйти».
 *
 * Навигация строится из КАНОНИЧЕСКОЙ структуры (по роли) ∩ разрешённые
 * модули бэкенда (`moduleKeys`). Лейблы — через @hsc/i18n.
 */
export interface AppShellProps {
  /** Роль актора (резолвится в Workspace из /v1/platform/me). */
  role: NavRole;
  /** Ключи модулей, разрешённых бэкендом (modules[].key). */
  moduleKeys: readonly string[];
  /** Название отеля (tenant.brand). В UI префиксуется «LovArt · ». */
  brand: string;
  /** Имя текущего пользователя (actor.name). */
  actorName: string;
  /** Email текущего пользователя (для карточки пользователя в сайдбаре). */
  actorEmail?: string;
  children: ReactNode;
}

/** Находит активный пункт навигации по текущему pathname (для крошки). */
function findActive(
  groups: readonly ResolvedNavGroup[],
  pathname: string,
): { group: ResolvedNavGroup; label: string } | null {
  let best: { group: ResolvedNavGroup; label: string; len: number } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const matches =
        item.path === '/'
          ? pathname === '/'
          : pathname === item.path || pathname.startsWith(`${item.path}/`);
      if (matches && (!best || item.path.length > best.len)) {
        best = { group, label: item.label, len: item.path.length };
      }
    }
  }
  return best ? { group: best.group, label: best.label } : null;
}

export function AppShell({
  role,
  moduleKeys,
  brand,
  actorName,
  actorEmail,
  children,
}: AppShellProps): JSX.Element {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const groups = useMemo(
    () => buildCanonicalNav(role, moduleKeys, t),
    [role, moduleKeys, t],
  );
  const tabs = useMemo(
    () => buildBottomTabs(role, moduleKeys, t),
    [role, moduleKeys, t],
  );

  const handleLogout = (): void => {
    clearLocalTokens();
    logout();
    navigate('/login', { replace: true });
  };

  const active = useMemo(() => findActive(groups, pathname), [groups, pathname]);
  const override = useShellHeaderOverride();
  const crumb = active
    ? `${active.group.label} / ${active.label}`
    : ROLE_LABEL[role];
  const title = override.title ?? active?.label ?? t('nav.dashboard');

  const brandFull = 'LovArt Hotels';
  const userInitials = initials(actorName);
  const roleText = ROLE_LABEL[role];

  return (
    <div className="ta-shell">
      <Sidebar
        groups={groups}
        brandFull={brandFull}
        hotelName={brand}
        actorName={actorName}
        actorEmail={actorEmail ?? roleText}
        userInitials={userInitials}
        logoutLabel={t('nav.logout')}
        onLogout={handleLogout}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <div className="ta-shell__main">
        {/* Планшетная узкая строка: гамбургер + бренд + «● Live» + аватар. */}
        <header className="ta-tabletbar" role="banner">
          <button
            type="button"
            className="ta-tabletbar__burger"
            aria-label="Меню"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
            data-testid="tablet-burger"
          >
            <span />
            <span />
            <span />
          </button>
          <span className="ta-tabletbar__brand">{brandFull}</span>
          <span className="ta-tabletbar__right">
            <span className="ta-chip ta-chip--ok">● Live</span>
            <span className="ta-avatar ta-avatar--sm" aria-hidden="true">
              {userInitials}
            </span>
          </span>
        </header>

        {/* Шапка контента (§2.2): крошка + заголовок страницы. */}
        <div className="ta-pagehead" data-testid="page-head">
          <div className="ta-pagehead__left">
            <div className="ta-pagehead__crumbs" data-testid="page-crumbs">
              {crumb}
            </div>
            <h2 className="ta-pagehead__title" data-testid="page-title">
              {title}
            </h2>
          </div>
          {override.hint ? (
            <div className="ta-pagehead__right">{override.hint}</div>
          ) : null}
        </div>

        <main className="ta-content">{children}</main>
      </div>

      <BottomTabs
        tabs={tabs}
        groups={groups}
        brand={brandFull}
        menuLabel={t('nav.menu')}
        drawerTitle={t('nav.drawerTitle')}
        actorName={actorName}
        actorMeta={actorEmail ?? roleText}
        userInitials={userInitials}
        logoutLabel={t('nav.logout')}
        onLogout={handleLogout}
      />
    </div>
  );
}
